/**
 * VISIBILITY CUSTOM ELEMENT
 * Abstracted from several other components that handle toggling state. Created for DRYness.
 */

export class VisibilityElement extends HTMLElement {
  handleShow: () => void
  handleHide: () => void
  handleToggle: () => void
  handleDisable: () => void
  handleEventDisable: (event: any) => void
  handleEventShow: (event: any) => void
  handleEventHide: (event: any) => void

  /**
   * Full-screen overlay panels belonging to this element, hidden with `display: none`
   * while closed.
   *
   * These panels stay in the DOM when closed and carry an opacity/transform transition,
   * which is exactly what makes WebKit promote them to their own compositor layer and
   * keep the backing store. At DPR 3 a full-screen layer is ~14MB, and a PDP carried 11
   * of them (~150MB). Ground truth from Safari Web Inspector on a tethered iPhone: 135
   * layers / 341MB of layer memory against only ~66MB of decoded images.
   *
   * Hiding them on the device took idle survival from 20-60s to 3min+, stopped the
   * product gallery crashing entirely, and allowed a full scroll cycle. `display: none`
   * is used rather than `content-visibility: hidden` because the latter is unsupported
   * on the older iOS versions this bug was first reported on.
   *
   * FAIL OPEN: if no panel is detected, or measurement has not run yet, panels are left
   * visible. A modal that misses the optimisation is harmless; one that will not open is
   * not. Opt out per element with `data-keep-painted`.
   */
  private overlayPanels: HTMLElement[] = []
  private panelHideTimeout: number | null = null
  /** Generous upper bound on the close transition; only affects when the layer is freed. */
  private static PANEL_HIDE_DELAY_MS = 600
  /** Safety cap on the one-off descendant scan in `collectOverlayPanels`. */
  private static MAX_PANEL_SCAN = 3000

  constructor() {
    super()

    // CUSTOM METHODS
    // Adding custom methods here to deal with 'this' issues

    this.handleShow = () => {
      // Must run BEFORE the state flip so the panel is rendered and has a start value
      // for its transition. Synchronous on purpose — see `revealOverlayPanels`.
      this.revealOverlayPanels()
      this.dataset.ui = 'visible'
    }

    this.handleHide = () => {
      this.dataset.ui = undefined
      this.scheduleOverlayPanelHide()
    }

    this.handleDisable = () => {
      this.dataset.ui = 'disabled'
    }

    this.handleToggle = () => {
      if (this.dataset.ui === 'visible') this.handleHide()
      else this.handleShow()
    }

    this.handleEventShow = (event) => {
      if (event.detail.target !== this.dataset.id) return
      this.handleShow()
    }

    this.handleEventHide = (event) => {
      if (event.detail.target !== this.dataset.id) return
      this.handleHide()
    }

    this.handleEventDisable = (event) => {
      if (event.detail.target !== this.dataset.id) return
      this.handleDisable()
    }
  }

  // Event Dispatchers
  static EVENT_SHOW = 'show'
  static dispatchShow(id: string) {
    document.dispatchEvent(
      new CustomEvent(VisibilityElement.EVENT_SHOW, {
        detail: {
          target: id
        }
      })
    )
  }

  static EVENT_HIDE = 'hide'
  static dispatchHide(id: string) {
    document.dispatchEvent(
      new CustomEvent(VisibilityElement.EVENT_HIDE, {
        detail: {
          target: id
        }
      })
    )
  }

  static EVENT_DISABLE = 'disable'
  static dispatchDisable(id: string) {
    document.dispatchEvent(
      new CustomEvent(VisibilityElement.EVENT_DISABLE, {
        detail: {
          target: id
        }
      })
    )
  }

  // The handlers are already instance properties bound to `this`. Calling
  // `.bind(this)` here would hand addEventListener and removeEventListener two
  // different function objects, so removal would silently never match and every
  // connect/disconnect cycle would leak another document listener.
  addEventListeners() {
    document.addEventListener(VisibilityElement.EVENT_SHOW, this.handleEventShow)
    document.addEventListener(VisibilityElement.EVENT_HIDE, this.handleEventHide)
    document.addEventListener(VisibilityElement.EVENT_DISABLE, this.handleEventDisable)
  }

  removeEventListeners() {
    document.removeEventListener(VisibilityElement.EVENT_SHOW, this.handleEventShow)
    document.removeEventListener(VisibilityElement.EVENT_HIDE, this.handleEventHide)
    document.removeEventListener(VisibilityElement.EVENT_DISABLE, this.handleEventDisable)
  }

  /**
   * Finds full-screen fixed panels belonging to this element. Deliberately shallow (two
   * levels): overlay panels in this theme are direct children of the element, and a full
   * descendant walk on every visibility element would be an expensive layout read.
   */
  private collectOverlayPanels() {
    // Full descendant scan, not the two levels this originally used. Measured on device:
    // the mini-cart panel (10.59MB) and the PLP filters drawer (11.88MB) both sit deeper
    // than two levels inside their modal and were being missed entirely.
    //
    // The cost is one pass of `getComputedStyle` per descendant, once, inside the rAF in
    // `connectedCallback` — not on any interaction path. Capped so a pathologically large
    // modal cannot stall the frame.
    const all = Array.from(this.querySelectorAll<HTMLElement>('*'))
    const candidates = all.length > VisibilityElement.MAX_PANEL_SCAN
      ? all.slice(0, VisibilityElement.MAX_PANEL_SCAN)
      : all

    this.overlayPanels = candidates.filter((el) => {
      if (el.hasAttribute('data-keep-painted')) return false
      // Only claim panels this element actually owns. Modals nest — the mobile nav
      // `x-modal.group/drawer` contains an `x-modal.group/submenu` per menu item — and a
      // plain descendant scan made the drawer claim all four submenu panels. Opening the
      // drawer then revealed them too, re-promoting ~88MB of layers exactly when the
      // shopper is navigating, and left two elements managing the same nodes.
      if (!this.ownsPanel(el)) return false

      const cs = getComputedStyle(el)
      // `absolute` as well as `fixed`: the header submenu panels are absolutely
      // positioned inside the drawer, and each costs ~22MB of layer on a phone.
      if (cs.position !== 'fixed' && cs.position !== 'absolute') return false

      const rect = el.getBoundingClientRect()
      // Ignore incidental small positioned elements — badges, close buttons, carets.
      if (rect.width < 40 || rect.height < 40) return false

      // The panel is not currently visible to the shopper if it sits wholly outside the
      // viewport or is transparent. Both are safe to stop painting: hiding something the
      // user cannot see cannot change what they see.
      //
      // This replaces an earlier "fixed AND covers >=90% of the viewport AND opacity 0"
      // test, which missed nearly everything that mattered. Measured on a real iPhone via
      // the Layers tab, the expensive panels are parked OFF-SCREEN with `translate-x-full`
      // / `translate-y-full` while fully opaque, and are sized `w-[87vw] max-w-[400px]`
      // (~87% of a 430pt viewport, just under the old threshold) or `h-[70dvh]` (fails on
      // height). Between them the nav drawer, four submenus, three size modals and the
      // mini cart accounted for roughly 199MB that the old rule never touched.
      const outsideViewport =
        rect.right <= 0 ||
        rect.bottom <= 0 ||
        rect.left >= window.innerWidth ||
        rect.top >= window.innerHeight
      const transparent = cs.opacity === '0' || cs.visibility === 'hidden'

      return outsideViewport || transparent
    })
  }

  /**
   * True when `el` belongs to this element and not to a nested visibility element.
   * A nested modal manages its own panels, so the outer one must leave them alone.
   *
   * Note this relies on the inner element already being upgraded. If it is not yet, the
   * outer element claims its panels — which is simply the previous behaviour, so the
   * failure mode is a missed optimisation rather than a broken modal.
   */
  private ownsPanel(el: HTMLElement): boolean {
    let node = el.parentElement
    while (node && node !== this) {
      if (node instanceof VisibilityElement) return false
      node = node.parentElement
    }
    return node === this
  }

  /**
   * Synchronous by design. An rAF here would mean a backgrounded or throttled tab could
   * leave a modal that never opens — failing closed, which is far worse than failing to
   * optimise. Forcing a reflow gives the transition its start value instead.
   */
  private revealOverlayPanels() {
    if (this.panelHideTimeout !== null) {
      window.clearTimeout(this.panelHideTimeout)
      this.panelHideTimeout = null
    }

    for (const panel of this.overlayPanels) {
      if (panel.style.display === 'none') {
        panel.style.display = ''
        // Flush layout so the browser registers the closed state before the class flip,
        // otherwise the opacity transition has nothing to animate from and it snaps.
        this.forceReflow(panel)
      }
    }
  }

  /** Reads a layout property purely for its side effect of flushing pending layout. */
  private forceReflow(el: HTMLElement): number {
    return el.offsetHeight
  }

  /** Frees the layer only once the close transition has finished. */
  private scheduleOverlayPanelHide() {
    if (!this.overlayPanels.length) return

    if (this.panelHideTimeout !== null) window.clearTimeout(this.panelHideTimeout)
    this.panelHideTimeout = window.setTimeout(() => {
      this.panelHideTimeout = null
      // Reopened while we waited — leave it alone.
      if (this.dataset.ui === 'visible') return
      for (const panel of this.overlayPanels) panel.style.display = 'none'
    }, VisibilityElement.PANEL_HIDE_DELAY_MS)
  }

  connectedCallback() {
    this.addEventListeners()

    // Measured after a frame so layout has settled. If this never runs (backgrounded
    // tab), panels simply stay painted — the fail-open path.
    requestAnimationFrame(() => {
      this.collectOverlayPanels()
      if (this.dataset.ui !== 'visible' && this.overlayPanels.length) {
        for (const panel of this.overlayPanels) panel.style.display = 'none'
      }
    })
  }

  disconnectedCallback() {
    this.removeEventListeners()

    if (this.panelHideTimeout !== null) {
      window.clearTimeout(this.panelHideTimeout)
      this.panelHideTimeout = null
    }
    // Leave no element stuck hidden if it is re-attached elsewhere.
    for (const panel of this.overlayPanels) panel.style.display = ''
    this.overlayPanels = []
  }
}
